import { useEffect, useMemo, useState } from 'react';
import { useHashRoute } from './router';
import { usePoses, setRuntimePoses } from './data/poses';
import { api } from './api/client';
import { useProgress } from './hooks/useProgress';
import { useRoutines } from './hooks/useRoutines';
import { Header } from './components/Header';
import { CatalogPage } from './components/CatalogPage';
import { PoseDetailPage } from './components/PoseDetailPage';
import { NewPosePage } from './components/NewPosePage';
import { RoutinesListPage } from './components/RoutinesListPage';
import { RoutineBuilderPage } from './components/RoutineBuilderPage';
import { GenerateRoutinePage } from './components/GenerateRoutinePage';
import { getBuiltinRoutine } from './data/starterRoutines';
import { SuggestButton } from './components/FeedbackPanel';
import { SignInCta } from './components/SignInCta';
import { HowItWorksIntro, introSeen, markIntroSeen } from './components/HowItWorks';
import { FeedbackAdminPage } from './components/FeedbackAdminPage';
import { RoutinePlayerPage } from './components/RoutinePlayerPage';
import { PublicRoutinePage } from './components/PublicRoutinePage';
import { PublicGalleryPage } from './components/PublicGalleryPage';
import { PoseImagesProvider } from './data/poseImages';

export default function App() {
  const route = useHashRoute();
  const progress = useProgress();
  const routines = useRoutines();
  const poses = usePoses();

  // Merge admin-created runtime poses into the catalog on load.
  useEffect(() => {
    api.listPoses().then(setRuntimePoses).catch(() => {});
  }, []);

  const learnedCount = useMemo(
    () => poses.filter((p) => progress.map[p.id] === 'learned').length,
    [poses, progress.map],
  );

  const poseMatch = route.match(/^\/pose\/(.+)$/);
  const editMatch = route.match(/^\/routines\/([^/]+)\/edit$/);
  const playMatch = route.match(/^\/routines\/([^/]+)\/play$/);
  const publicMatch = route.match(/^\/r\/([^/]+)$/);

  let content;
  if (publicMatch) {
    content = (
      <PublicRoutinePage
        id={decodeURIComponent(publicMatch[1])}
        routines={routines}
        progress={progress}
      />
    );
  } else if (poseMatch) {
    content = (
      <PoseDetailPage
        id={decodeURIComponent(poseMatch[1])}
        progress={progress}
        routines={routines}
      />
    );
  } else if (route === '/routines/new') {
    content = <RoutineBuilderPage routines={routines} />;
  } else if (route === '/routines/generate') {
    content = <GenerateRoutinePage routines={routines} />;
  } else if (editMatch) {
    const rid = decodeURIComponent(editMatch[1]);
    const builtin = getBuiltinRoutine(rid);
    // Starter routines are read-only: "Customize" seeds a fresh, editable copy
    // (saved as the user's own on Save) rather than editing the original.
    content = builtin ? (
      <RoutineBuilderPage
        routines={routines}
        initialName={`${builtin.name} (my copy)`}
        initialSteps={builtin.steps}
      />
    ) : (
      <RoutineBuilderPage routines={routines} routineId={rid} />
    );
  } else if (playMatch) {
    content = (
      <RoutinePlayerPage
        routines={routines}
        progress={progress}
        routineId={decodeURIComponent(playMatch[1])}
      />
    );
  } else if (route === '/discover') {
    content = <PublicGalleryPage />;
  } else if (route === '/admin/new-pose') {
    content = <NewPosePage />;
  } else if (route === '/admin/feedback') {
    content = <FeedbackAdminPage />;
  } else if (route === '/routines') {
    content = <RoutinesListPage routines={routines} />;
  } else {
    content = <CatalogPage progress={progress} />;
  }

  const isPlayer = Boolean(playMatch);

  // One prompt at a time: explain the app first, then invite them to keep it.
  const [seenIntro, setSeenIntro] = useState(introSeen);
  const dismissIntro = () => {
    markIntroSeen();
    setSeenIntro(true);
  };

  return (
    <PoseImagesProvider>
      <Header route={route} learned={learnedCount} total={poses.length} />
      <main className={`wrap page${isPlayer ? ' page-full' : ''}`}>
        {/* Not while a routine is playing — that screen is for the mat, not for us. */}
        {!isPlayer &&
          (seenIntro ? (
            <SignInCta />
          ) : (
            <HowItWorksIntro poseCount={poses.length} onDismiss={dismissIntro} />
          ))}
        {content}
      </main>
      <footer className="site-footer">
        Yoga Me · Tier 1 seed content from <code>docs/seed/poses.json</code>
        <SuggestButton className="linklike footer-suggest" />
      </footer>
    </PoseImagesProvider>
  );
}
